import {
  ChevronIcon,
  CloseIcon,
  defaultTextVariants,
  IconButton,
  Text,
} from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useModalSurface } from "./useModalSurface";
import { FULLSCREEN_WINDOW_Z } from "./windowLayers";
import styles from "./FullscreenWindow.module.css";

/**
 * 스텝 헤더를 좌우로 이동한다. 지정하면 셸이 스텝 헤더 양옆에 [←] [→] 버튼을 그리고 `←`/`→`
 * 키도 받는다. 셸은 어느 스텝이 이웃인지 모르므로 호출부가 두 동작만 전달한다.
 * 양 끝에서는 버튼을 disabled 로 두어 더 이동할 곳이 없음을 표시한다(순환하지 않는다).
 */
export type FullscreenWindowStepNav = {
  onPrev: () => void;
  onNext: () => void;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  // 접근성 이름이자 툴팁이다. 이동 대상은 호출부만 안다.
  prevLabel: string;
  nextLabel: string;
};

/**
 * 본문이 셸이 제공한 남은 높이를 쓰는 방식이다. 지정하지 않으면 `scroll` 이다.
 * - `scroll`: 셸이 스크롤 컨테이너가 된다. 자식들은 행으로 쌓이고 간격은 셸의 gap 이 정한다.
 *   본문 높이가 확정돼 있으므로 자식이 `height: 100%` 로 본문을 채울 수도 있다.
 * - `fill`: 셸은 스크롤하지 않고 높이만 제공한다. 스크롤 영역은 본문이 직접 소유한다
 *   (예: 2단 레이아웃에서 한쪽만 스크롤하는 창, 자체 `Scrollbar` 를 쓰는 창).
 */
export type FullscreenWindowBodyLayout = "scroll" | "fill";

export type FullscreenWindowProps = {
  // 표시 여부(controlled)다.
  open: boolean;
  // 헤더 좌측 제목이다(예: 데이터셋명). accessible name(aria-label) 으로도 쓴다.
  title: string;
  // 제목 오른쪽 배지 슬롯이다(선택). 이 창이 **보고 있는 대상**의 상태를 둔다(예: 데이터셋 파생
  // 처리 중). 우측 액션 슬롯과의 구분 기준은 조작 가능 여부다. 누를 수 있으면 headerActions 에,
  // 읽기만 하면 여기에 둔다. 제목은 이 배지가 차지한 폭만큼 더 일찍 말줄임된다.
  titleBadge?: ReactNode;
  // 제목 아래 스텝 헤더 슬롯이다(예: 1 Embodiment · 2 Labels · 3 Confirm). 선택.
  stepHeader?: ReactNode;
  // 스텝 헤더 양옆 이동 버튼과 `←`/`→` 키다. 지정하지 않으면 버튼과 키 바인딩이 모두 없다.
  // 셸이 이 값을 처리하고 내용으로 전달하지 않는다.
  stepNav?: FullscreenWindowStepNav;
  /**
   * `↑`/`↓` 는 내용이 받는다. 세로 이동 대상은 창마다 다르다(예: 섹션 목록, 에피소드 목록).
   * 지정하지 않으면 셸이 그 키를 처리하지 않는다. 그래서 본문이 자체 리스너로 화살표를 쓰는 창과
   * 충돌하지 않는다. 입력창·수식키·이미 소비된 키의 양보는 셸이 소유하므로 콜백은 이동만 하면 된다.
   */
  onArrowUp?: () => void;
  onArrowDown?: () => void;
  // 우측 윈도우 컨트롤(최소화/닫기) 왼쪽에 두는 페이지 액션 슬롯이다(예: 북마크·archive). 선택.
  headerActions?: ReactNode;
  // [_] 최소화다. 창을 전역 dock 으로 접는다. 없으면 버튼을 그리지 않는다. 작업표시줄에 항목이
  // 없는 중첩 창은 최소화하면 되돌릴 방법이 없다.
  onMinimize?: () => void;
  // [X] 종료 요청이다. 호출부가 ConfirmModal 로 확인한다. Escape 도 이 콜백에 매핑한다.
  onRequestClose: () => void;
  // 바깥(백드롭)을 클릭하면 호출한다. 지정하지 않으면 백드롭 클릭은 무시한다. 셸은 정책(닫기/최소화)을
  // 모르고 호출부가 정한다. 데이터셋 창은 최소화에 연결한다.
  onBackdrop?: () => void;
  // Dialog 본문이다.
  children: ReactNode;
  // 본문 아래 고정 푸터 슬롯이다(예: 확정 버튼). 지정하지 않으면 푸터 바를 그리지 않는다.
  footer?: ReactNode;
  // Accessible name 이 title 과 달라야 할 때 쓴다(선택). 기본값은 title 이다.
  ariaLabel?: string;
  // 본문의 높이·스크롤 계약이다. 지정하지 않으면 "scroll" 이다.
  bodyLayout?: FullscreenWindowBodyLayout;
  // 윈도우 컨트롤 버튼 라벨이다(접근성·툴팁). 기본값은 한국어다.
};

// 전체화면 창 셸이다. **c6 Modal 이 아니라** body portal 커스텀 오버레이다. c6 Modal 은 중앙 dialog
// 셸(backdrop·모션·마크업)을 전제한다. 그래서 전체화면 창 프레임(헤더 그리드·푸터 바·최소화)을
// 표현할 수 없다. 대신 c6 modalStack 에 직접 등록해 c6 Modal 과 같은 규칙을 따른다:
// - scroll lock·바깥 inert 는 스택이 소유한다. 중첩 c6 Modal 과 ref-count/topmost 가 충돌하지 않는다.
// - dialog 를 popup mount node 로 등록한다. 그래서 창 안 AnchoredPopup 계열(예: 드롭다운 메뉴)이
//   dialog 안으로 portal 된다. body 로 portal 되면 popover z(20) < 오버레이 z(880)이므로 메뉴가 창 뒤에 가려진다.
// - 중첩 c6 Modal 은 body 레벨 형제(z 1000~)이므로 이 오버레이 위에 쌓인다. 중첩 Modal 이 열려
//   있는 동안 스택이 이 오버레이를 inert 로 만든다.
export function FullscreenWindow({
  open,
  title,
  titleBadge,
  stepHeader,
  stepNav,
  onArrowUp,
  onArrowDown,
  headerActions,
  onMinimize,
  onRequestClose,
  onBackdrop,
  children,
  footer,
  ariaLabel,
  bodyLayout = "scroll",
}: FullscreenWindowProps): ReactNode {
  const zIndex = FULLSCREEN_WINDOW_Z;
  const { container, dialogRef, onBackdropClick, onBackdropMouseDown } = useModalSurface({
    active: open,
    rootAttributeName: "data-fullscreen-window-root",
    zIndex,
    onRequestClose,
    onBackdrop,
    // 좌/우는 셸이 처리한다. 위/아래는 내용의 이동 정책을 콜백으로만 받는다.
    hotkeys: [
      { key: "ArrowLeft", onPress: stepNav?.prevDisabled === true ? undefined : stepNav?.onPrev },
      { key: "ArrowRight", onPress: stepNav?.nextDisabled === true ? undefined : stepNav?.onNext },
      { key: "ArrowUp", onPress: onArrowUp },
      { key: "ArrowDown", onPress: onArrowDown },
    ],
  });

  if (!open || container === null) {
    return null;
  }

  return createPortal(
    <div
      className={styles.overlay}
      style={{ zIndex }}
      // Scrim 은 표시용이다. 바깥 클릭은 onBackdrop(최소화) 편의 동작이다. 키보드는 Escape(종료)로 처리한다.
      role="presentation"
      onMouseDown={onBackdropMouseDown}
      onClick={onBackdropClick}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel ?? title}
        tabIndex={-1}
        ref={dialogRef}
      >
        {/* 3분할이다: 제목(좌) · 스텝 헤더(가운데 정렬) · 윈도우 컨트롤(우). 가운데 auto 컬럼은
            항상 렌더해 컨트롤을 우측 컬럼에 고정한다(stepHeader 가 없어도 빈 셀을 유지한다). */}
        <header className={styles.header}>
          <div className={styles.titleGroup}>
            <Text variant={defaultTextVariants["body-bold"]} className={styles.title}>
              {title}
            </Text>
            {titleBadge}
          </div>
          {/* 스텝 헤더와 그 양옆 이동 버튼이다. 글리프는 셰브론 하나이고 테두리도 버튼만 가진다.
              키캡을 함께 두면 키캡 상자와 버튼이 겹쳐 테두리가 두 겹으로 보인다. 단축키는
              글리프 대신 툴팁이 표시한다. 좌/우는 같은 셰브론을 반대로 회전해 모양을 맞춘다. */}
          <div className={styles.stepSlot}>
            {stepNav ? (
              <IconButton
                aria-label={stepNav.prevLabel}
                title={`${stepNav.prevLabel} (←)`}
                type="button"
                // 우측 윈도우 컨트롤과 같은 테두리다. 양 끝의 disabled 상태도 같은 규칙으로 보인다.
                variant="surface"
                size="sm"
                disabled={stepNav.prevDisabled}
                onClick={stepNav.onPrev}
              >
                <ChevronIcon className={styles.stepNavCaretPrev} />
              </IconButton>
            ) : null}
            {stepHeader}
            {stepNav ? (
              <IconButton
                aria-label={stepNav.nextLabel}
                title={`${stepNav.nextLabel} (→)`}
                type="button"
                variant="surface"
                size="sm"
                disabled={stepNav.nextDisabled}
                onClick={stepNav.onNext}
              >
                <ChevronIcon className={styles.stepNavCaretNext} />
              </IconButton>
            ) : null}
          </div>
          <div className={styles.headerRight}>
            {headerActions ? <div className={styles.headerActions}>{headerActions}</div> : null}
            <div className={styles.windowControls}>
              {onMinimize ? (
                <IconButton
                  aria-label="최소화"
                  title="창을 최소화합니다."
                  type="button"
                  variant="surface"
                  size="sm"
                  onClick={onMinimize}
                >
                  <MinimizeIcon />
                </IconButton>
              ) : null}
              <IconButton
                aria-label="종료"
                title="창을 종료합니다."
                type="button"
                variant="surface"
                size="sm"
                onClick={onRequestClose}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        </header>
        <div
          className={[
            styles.body,
            bodyLayout === "fill" ? styles.bodyFill : styles.bodyScroll,
          ].join(" ")}
        >
          {children}
        </div>
        {footer ? <footer className={styles.footer}>{footer}</footer> : null}
      </div>
    </div>,
    container,
  );
}

function MinimizeIcon() {
  return (
    <svg aria-hidden="true" width="1em" height="1em" viewBox="0 0 16 16">
      <path
        d="M3.5 12h9"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.5"
      />
    </svg>
  );
}
